import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

type Product = {
  id: string;
  productCode: string;
  barcode: string;
  name: string;
  brand: string;
  model: string;
  categoryId: string;
  purchasePrice: number;
  salePrice: number;
  stock: number;
  imageUrl: string;
};

type CartItem = { product: Product; quantity: number; customPrice: number };
type PaymentMethod = "nakit" | "pos" | "cari" | "kismi_odeme";
type CheckoutStep = "payment" | "room-info" | "complete";

type CategoryOption = {
  id: string;
  name: string;
  icon: string;
};

const defaultCategories: CategoryOption[] = [
  { id: "all", name: "Tümü", icon: "ri-apps-line" },
];

const categoryIconMap: Record<string, string> = {
  "İçecek": "ri-goblet-line",
  "Sıcak İçecek": "ri-cup-line",
  "Ana Yemek": "ri-restaurant-line",
  "Çorba": "ri-bowl-line",
  "Ara Sıcak": "ri-fire-line",
  "Tatlı": "ri-cake-line",
  "İcecek": "ri-goblet-line",
  "Sicak Icecek": "ri-cup-line",
  "Ana Yemek": "ri-restaurant-line",
  "Corba": "ri-bowl-line",
  "Ara Sicak": "ri-fire-line",
  "Tatli": "ri-cake-line",
};

const paymentMethods = [
  { id: "nakit" as PaymentMethod, label: "Nakit", icon: "ri-money-dollar-circle-line", color: "text-emerald-600", bg: "bg-emerald-100", border: "border-emerald-200" },
  { id: "pos" as PaymentMethod, label: "Kredi Kartı", icon: "ri-bank-card-line", color: "text-amber-600", bg: "bg-amber-100", border: "border-amber-200" },
  { id: "cari" as PaymentMethod, label: "Oda Hesabı", icon: "ri-hotel-bed-line", color: "text-rose-600", bg: "bg-rose-100", border: "border-rose-200" },
  { id: "kismi_odeme" as PaymentMethod, label: "Kısmi Ödeme", icon: "ri-wallet-3-line", color: "text-violet-600", bg: "bg-violet-100", border: "border-violet-200" },
];

export default function RestaurantPosPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId;

  const [query, setQuery] = useState("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>("payment");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [roomNumber, setRoomNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [cartPanelOpen, setCartPanelOpen] = useState(false);
  const [saleComplete, setSaleComplete] = useState(false);
  const [partialAmount, setPartialAmount] = useState("");
  const [roomError, setRoomError] = useState("");
  const [saveError, setSaveError] = useState("");
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [addCategoryError, setAddCategoryError] = useState("");

  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [addProductError, setAddProductError] = useState("");
  const [newProduct, setNewProduct] = useState({
    name: "",
    barcode: "",
    categoryId: "",
    salePrice: "",
    purchasePrice: "",
    stock: "",
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>(defaultCategories);
  const [dataLoading, setDataLoading] = useState(true);
  const [dbCategories, setDbCategories] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const loadDealerData = useCallback(async () => {
    if (!dealerId) {
      setProducts([]);
      setCategories([...defaultCategories]);
      setDataLoading(false);
      return;
    }

    const supabase = getSupabase();
    try {
      const [prodRes, catRes] = await Promise.all([
        supabase.from("products").select("*").eq("dealer_id", dealerId),
        supabase.from("categories").select("*").eq("dealer_id", dealerId),
      ]);

      if (prodRes.data && prodRes.data.length > 0) {
        setProducts(
          prodRes.data
            .filter((p: Record<string, unknown>) => p.product_code !== "REST-001")
            .map((p: Record<string, unknown>) => ({
            id: p.id as string,
            productCode: (p.product_code as string) || "",
            barcode: (p.barcode as string) || "",
            name: (p.name as string) || "",
            brand: (p.brand as string) || "",
            model: (p.model as string) || "",
            categoryId: (p.category_id as string) || "",
            purchasePrice: Number(p.purchase_price) || 0,
            salePrice: Number(p.sale_price) || 0,
            stock: Number(p.stock) || 0,
            imageUrl: (p.image_url as string) || "",
          }))
        );
      } else {
        setProducts([]);
      }

      if (catRes.data && catRes.data.length > 0) {
        const dbCats = (catRes.data as Array<Record<string, unknown>>).map((c) => ({
          id: c.id as string,
          name: (c.name as string) || "",
        }));
        setDbCategories(dbCats);

        const mapped: CategoryOption[] = [
          { id: "all", name: "Tümü", icon: "ri-apps-line" },
          ...dbCats.map((c) => ({
            id: c.id,
            name: c.name,
            icon: categoryIconMap[c.name] || "ri-price-tag-3-line",
          })),
        ];
        setCategories(mapped);
      } else {
        setCategories([...defaultCategories]);
      }
    } catch {
      setProducts([]);
      setCategories([...defaultCategories]);
    } finally {
      setDataLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    loadDealerData();
  }, [loadDealerData]);

  const openAddCategoryModal = () => {
    setNewCategoryName("");
    setAddCategoryError("");
    setShowAddCategoryModal(true);
  };

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      setAddCategoryError("Lütfen kategori adı girin!");
      return;
    }
    if (dbCategories.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      setAddCategoryError("Bu isimde bir kategori zaten var!");
      return;
    }
    if (!dealerId) {
      setAddCategoryError("Bayi bilgisi bulunamadı.");
      return;
    }
    setSavingCategory(true);
    setAddCategoryError("");
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("categories")
        .insert({ name, dealer_id: dealerId })
        .select("id, name")
        .single();

      if (error) throw error;

      const newCat = { id: data.id as string, name: data.name as string };
      setDbCategories((prev) => [...prev, newCat]);
      setCategories((prev) => [
        ...prev,
        { id: newCat.id, name: newCat.name, icon: categoryIconMap[newCat.name] || "ri-price-tag-3-line" },
      ]);
      logAction(dealerId, user?.id || null, LOG_ACTIONS.CATEGORY_ADD, `Kategori eklendi: ${name}`);
      setShowAddCategoryModal(false);
    } catch (e: any) {
      setAddCategoryError(e.message || "Kategori eklenirken hata oluştu.");
    } finally {
      setSavingCategory(false);
    }
  };

  const openAddProductModal = () => {
    setNewProduct({
      name: "",
      barcode: "",
      categoryId: dbCategories[0]?.id || "",
      salePrice: "",
      purchasePrice: "",
      stock: "",
    });
    setAddProductError("");
    setShowAddProductModal(true);
  };

  const addProduct = async () => {
    const name = newProduct.name.trim();
    const salePrice = parseFloat(newProduct.salePrice);
    if (!name) {
      setAddProductError("Lütfen ürün adı girin!");
      return;
    }
    if (!newProduct.salePrice || isNaN(salePrice) || salePrice <= 0) {
      setAddProductError("Lütfen geçerli bir satış fiyatı girin!");
      return;
    }
    if (!dealerId) {
      setAddProductError("Bayi bilgisi bulunamadı.");
      return;
    }
    setSavingProduct(true);
    setAddProductError("");
    try {
      const supabase = getSupabase();
      const purchasePrice = newProduct.purchasePrice ? parseFloat(newProduct.purchasePrice) : 0;
      const stock = newProduct.stock ? parseInt(newProduct.stock, 10) : 0;

      const { data, error } = await supabase
        .from("products")
        .insert({
          name,
          barcode: newProduct.barcode.trim() || null,
          category_id: newProduct.categoryId || null,
          sale_price: salePrice,
          purchase_price: purchasePrice,
          stock,
          dealer_id: dealerId,
        })
        .select("id, product_code, barcode, name, brand, model, category_id, purchase_price, sale_price, stock, image_url")
        .single();

      if (error) throw error;

      const p = data as Record<string, unknown>;
      const newProd: Product = {
        id: p.id as string,
        productCode: (p.product_code as string) || "",
        barcode: (p.barcode as string) || "",
        name: (p.name as string) || "",
        brand: (p.brand as string) || "",
        model: (p.model as string) || "",
        categoryId: (p.category_id as string) || "",
        purchasePrice: Number(p.purchase_price) || 0,
        salePrice: Number(p.sale_price) || 0,
        stock: Number(p.stock) || 0,
        imageUrl: (p.image_url as string) || "",
      };
      setProducts((prev) => [...prev, newProd]);
      logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_ADD, `Ürün eklendi: ${name} (₺${salePrice.toFixed(2)})`);
      setShowAddProductModal(false);
    } catch (e: any) {
      setAddProductError(e.message || "Ürün eklenirken hata oluştu.");
    } finally {
      setSavingProduct(false);
    }
  };

  const filteredProducts = useMemo(() => {
    let prods = products;
    if (query.trim()) {
      const q = query.toLowerCase();
      prods = prods.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.barcode.includes(q) ||
          p.productCode.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== "all") {
      prods = prods.filter((p) => p.categoryId === selectedCategory);
    }
    return prods;
  }, [products, query, selectedCategory]);

  useEffect(() => {
    barcodeInputRef.current?.focus();
  }, []);

  const totalAmount = cart.reduce((sum, item) => sum + item.customPrice * item.quantity, 0);

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product, quantity: 1, customPrice: product.salePrice }];
    });
    setQuery("");
    barcodeInputRef.current?.focus();
  };

  const handleBarcodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && query.trim()) {
      const found = products.find(
        (p) => p.barcode === query.trim() || p.productCode.toLowerCase() === query.trim().toLowerCase()
      );
      if (found) {
        addToCart(found);
      }
    }
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.product.id !== productId) return item;
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }).filter(Boolean) as CartItem[]
    );
  };

  const openCheckout = () => {
    if (cart.length === 0) return;
    setCheckoutStep("payment");
    setPaymentMethod(null);
    setRoomNumber("");
    setCustomerName("");
    setPartialAmount("");
    setRoomError("");
    setSaveError("");
    setShowCheckout(true);
  };

  const goToRoomInfo = () => {
    if (!paymentMethod) return;
    setCheckoutStep("room-info");
    setRoomError("");
  };

  const confirmSale = async () => {
    const missingFields: string[] = [];
    if (!roomNumber.trim()) missingFields.push("Oda Numarası");
    if (!customerName.trim()) missingFields.push("Müşteri Adı");
    if (missingFields.length > 0) {
      setRoomError(`${missingFields.join(" ve ")} alanı doldurulmak zorundadır!`);
      return;
    }
    setRoomError("");
    setSaveError("");

    if (!dealerId) {
      setCheckoutStep("complete");
      setSaleComplete(true);
      return;
    }

    setSaving(true);
    try {
      const supabase = getSupabase();
      const { data: saleData, error: saleError } = await supabase
        .from("sales")
        .insert({
          dealer_id: dealerId,
          payment_method: paymentMethod,
          total_amount: totalAmount,
          room_number: roomNumber || null,
          customer_name: customerName || null,
          staff_id: user?.id || null,
          sale_type: "restaurant",
          payment_status: (paymentMethod === "cari" || paymentMethod === "kismi_odeme") ? "pending" : "completed",
        })
        .select("id")
        .single();

      if (saleError) {
        setSaveError("Satış kaydedilirken hata oluştu: " + saleError.message);
        setSaving(false);
        return;
      }

      if (saleData) {
        const saleItems = cart.map((item) => ({
          sale_id: saleData.id,
          product_id: item.product.id,
          quantity: item.quantity,
          unit_price: item.customPrice,
          purchase_price: item.product.purchasePrice,
          description: item.product.name,
        }));

        const { error: itemsError } = await supabase.from("sale_items").insert(saleItems);
        if (itemsError) {
          setSaveError("Satış kalemleri kaydedilirken hata: " + itemsError.message);
          setSaving(false);
          return;
        }
      }

      setCheckoutStep("complete");
      setSaleComplete(true);

      logAction(dealerId, user?.id || null, LOG_ACTIONS.SALE_CREATE,
        `${cart.length} ürün · ₺${totalAmount.toFixed(2)} · ${paymentLabel} · Oda:${roomNumber} · Müşteri:${customerName}`
      );
    } catch {
      setSaveError("Bağlantı hatası. Lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  const resetSale = () => {
    setCart([]);
    setShowCheckout(false);
    setSaleComplete(false);
    setCheckoutStep("payment");
    setPaymentMethod(null);
    setRoomNumber("");
    setCustomerName("");
    setPartialAmount("");
    setRoomError("");
    setSaveError("");
    setQuery("");
    setSelectedCategory("all");
    barcodeInputRef.current?.focus();
    loadDealerData();
  };

  const paymentLabel = paymentMethods.find((m) => m.id === paymentMethod)?.label || "Bilinmiyor";
  const actingStaffName = user?.fullName || user?.username || user?.dealerName || "Bilinmiyor";
  const actingStaffRole = user?.role || (user?.loginType === "dealer" ? "Bayi Yetkilisi" : "");

  if (dataLoading) {
    return (
      <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="text-center text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-4xl block mb-3"></i>
          <p className="text-sm">Bayi verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">
            <a id="restaurant-pos-title">Restoran Hızlı Satış</a>
          </h4>
          <p className="text-sm text-foreground-500 mt-0.5">
            {user?.dealerName ? `${user.dealerName} · ` : ""}Barkod okutun veya ürün seçin, hızlıca satış yapın.
          </p>
        </div>
        {saleComplete && (
          <button
            onClick={resetSale}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> Yeni Satış
          </button>
        )}
      </div>

      {/* Main area */}
      <div className="flex-1 flex gap-5 min-h-0">
        {/* Left: Product area */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {/* Barcode Input */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 mb-4 shrink-0">
            <div className="relative">
              <i className="ri-barcode-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                ref={barcodeInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleBarcodeKeyDown}
                placeholder="Barkod okutun veya ürün adı girin... (Enter ile sepete ekleyin)"
                className="w-full pl-11 pr-4 py-3 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 font-mono tracking-wide"
                autoFocus
              />
              {query.trim() && (
                <button
                  onClick={() => { setQuery(""); barcodeInputRef.current?.focus(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-200/70 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            {query.trim() && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {filteredProducts.slice(0, 6).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addToCart(p)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg text-xs font-medium text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line text-sm"></i>
                    {p.name}
                    <span className="text-primary-500">₺{p.salePrice}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="flex items-center gap-1.5 mb-3 shrink-0">
            <div className="flex gap-1.5 overflow-x-auto pb-1 flex-1 min-w-0">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    selectedCategory === cat.id
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={openAddCategoryModal}
                className="flex items-center gap-1 px-2.5 py-2 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium hover:bg-secondary-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-sm"></i> Kategori Ekle
              </button>
              <button
                onClick={openAddProductModal}
                className="flex items-center gap-1 px-2.5 py-2 bg-primary-100 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-sm"></i> Ürün Ekle
              </button>
            </div>
          </div>

          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {saleComplete ? (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 text-center h-full flex flex-col items-center justify-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-5">
                  <i className="ri-check-line text-emerald-600 text-4xl"></i>
                </div>
                <h4 className="text-xl font-semibold text-foreground-950 mb-2">Satış Başarıyla Tamamlandı!</h4>
                <p className="text-foreground-500 mb-1">
                  Toplam: <strong className="text-foreground-950">₺{totalAmount.toFixed(2)}</strong>
                </p>
                <p className="text-sm text-foreground-400">Ödeme: {paymentLabel} · Oda: {roomNumber} · Müşteri: {customerName}</p>
                <p className="text-sm text-foreground-400">Personel: {actingStaffName}{actingStaffRole ? ` (${actingStaffRole})` : ""}</p>
              </div>
            ) : products.length === 0 ? (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 text-center h-full flex flex-col items-center justify-center">
                <i className="ri-box-3-line text-5xl text-foreground-300 block mb-4"></i>
                <h4 className="text-base font-semibold text-foreground-700 mb-2">Henüz Ürün Eklenmemiş</h4>
                <p className="text-sm text-foreground-400 max-w-sm">
                  Bu bayi için henüz ürün tanımlanmamış. Sağ üstteki "Ürün Ekle" butonuyla hemen buradan ürün ekleyebilir, ya da admin panelinden/bayi yönetiminden ekleyebilirsiniz.
                </p>
              </div>
            ) : filteredProducts.length === 0 && query.trim() ? (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 text-center text-foreground-400">
                <i className="ri-search-line text-4xl block mb-3"></i>
                <p className="text-sm">Aramanızla eşleşen ürün bulunamadı.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {filteredProducts.map((product) => {
                  const isOutOfStock = (product.stock || 0) <= 0;
                  const cat = categories.find((c) => c.id === product.categoryId);
                  return (
                    <button
                      key={product.id}
                      onClick={() => !isOutOfStock && addToCart(product)}
                      disabled={isOutOfStock}
                      className={`bg-background-50 border rounded-xl p-3 text-left transition-all cursor-pointer ${
                        isOutOfStock
                          ? "border-background-200/50 opacity-50 cursor-not-allowed"
                          : "border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 active:scale-[0.98]"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isOutOfStock ? "bg-background-100" : "bg-primary-50"
                        }`}>
                          <i className={`${isOutOfStock ? "ri-prohibited-line text-foreground-300" : cat?.icon || "ri-box-3-line"} text-lg ${isOutOfStock ? "" : "text-primary-500"}`}></i>
                        </div>
                        {isOutOfStock && (
                          <span className="px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] rounded-full font-medium">Tükendi</span>
                        )}
                        {!isOutOfStock && (product.stock || 0) <= 5 && (
                          <span className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] rounded-full font-medium">Son {product.stock}</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-foreground-900 truncate mb-1">{product.name}</p>
                      <p className="text-xs text-foreground-400 truncate mb-2">{product.productCode}</p>
                      <p className={`text-sm font-bold ${isOutOfStock ? "text-foreground-300" : "text-primary-600"}`}>
                        ₺{product.salePrice.toFixed(2)}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Cart Panel (Desktop) */}
        {!saleComplete && (
          <div className="hidden lg:flex lg:flex-col w-80 shrink-0">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-4 shrink-0">
                <i className="ri-shopping-cart-2-line text-lg text-foreground-700"></i>
                <h4 className="font-semibold text-foreground-950 text-sm"><a id="cart-title">Sepet</a></h4>
                <span className="ml-auto text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                  {cart.length} ürün
                </span>
              </div>

              {cart.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-foreground-400">
                  <i className="ri-shopping-cart-line text-4xl block mb-3"></i>
                  <p className="text-sm">Sepet boş</p>
                  <p className="text-xs mt-1">Ürün eklemek için barkod okutun veya ürün seçin</p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto space-y-2 mb-3 min-h-0">
                    {cart.map((item) => (
                      <div key={item.product.id} className="bg-background-100 rounded-lg p-2.5 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-medium text-foreground-900 flex-1 truncate">{item.product.name}</span>
                          <button
                            onClick={() => removeFromCart(item.product.id)}
                            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer shrink-0"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => updateQuantity(item.product.id, -1)}
                              className="w-7 h-7 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer"
                            >
                              <i className="ri-subtract-line text-sm"></i>
                            </button>
                            <span className="w-7 text-center text-sm font-medium text-foreground-900">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.product.id, 1)}
                              className="w-7 h-7 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 cursor-pointer"
                            >
                              <i className="ri-add-line text-sm"></i>
                            </button>
                          </div>
                          <span className="text-sm font-semibold text-foreground-900">
                            ₺{(item.customPrice * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-background-200/70 pt-3 mb-3 shrink-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground-950">Toplam</span>
                      <span className="text-lg font-bold text-foreground-950">₺{totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    onClick={openCheckout}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Satışı Tamamla
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Cart Toggle */}
      {!saleComplete && cart.length > 0 && (
        <div className="lg:hidden fixed bottom-20 right-4 z-30">
          <button
            onClick={() => setCartPanelOpen(true)}
            className="relative w-14 h-14 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-2xl flex items-center justify-center shadow-lg transition-all cursor-pointer active:scale-[0.95]"
          >
            <i className="ri-shopping-cart-2-line text-xl"></i>
            <span className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-accent-500 text-background-50 text-[11px] font-bold rounded-full flex items-center justify-center">
              {cart.length}
            </span>
          </button>
        </div>
      )}

      {/* Mobile Cart Panel */}
      {cartPanelOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setCartPanelOpen(false)}>
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl max-h-[70vh] flex flex-col animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-background-200/70 shrink-0">
              <h4 className="font-semibold text-foreground-950">Sepet ({cart.length} ürün)</h4>
              <button onClick={() => setCartPanelOpen(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {cart.map((item) => (
                <div key={item.product.id} className="bg-background-100 rounded-lg p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-foreground-900 flex-1 truncate">{item.product.name}</span>
                    <button onClick={() => removeFromCart(item.product.id)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 cursor-pointer shrink-0">
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQuantity(item.product.id, -1)} className="w-8 h-8 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 cursor-pointer">
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="w-8 text-center text-sm font-medium text-foreground-900">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product.id, 1)} className="w-8 h-8 rounded-md bg-background-50 border border-background-200/70 flex items-center justify-center text-foreground-500 cursor-pointer">
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">₺{(item.customPrice * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-background-200/70 space-y-3 shrink-0">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground-950">Toplam</span>
                <span className="text-lg font-bold text-foreground-950">₺{totalAmount.toFixed(2)}</span>
              </div>
              <button
                onClick={() => { setCartPanelOpen(false); openCheckout(); }}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Satışı Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/30" onClick={() => setShowCheckout(false)}>
          <div className="bg-background-50 rounded-t-2xl lg:rounded-2xl p-6 w-full max-w-lg mx-0 lg:mx-4 max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-5">
              {[
                { step: "payment", num: 1, label: "Ödeme" },
                { step: "room-info", num: 2, label: "Oda/Müşteri" },
              ].map((s, idx) => (
                <div key={s.step} className="flex items-center gap-2 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    checkoutStep === s.step
                      ? "bg-primary-500 text-background-50"
                      : checkoutStep === "complete" || (idx < ["payment", "room-info"].indexOf(checkoutStep))
                        ? "bg-emerald-500 text-background-50"
                        : "bg-background-100 text-foreground-400 border border-background-200/70"
                  }`}>
                    {checkoutStep === "complete" || (idx < ["payment", "room-info"].indexOf(checkoutStep)) ? (
                      <i className="ri-check-line"></i>
                    ) : (
                      s.num
                    )}
                  </div>
                  <span className={`text-xs font-medium whitespace-nowrap ${
                    checkoutStep === s.step ? "text-foreground-900" : "text-foreground-400"
                  }`}>{s.label}</span>
                  {idx < 1 && <div className="flex-1 h-px bg-background-200/70"></div>}
                </div>
              ))}
            </div>

            {/* Step 1: Payment Method */}
            {checkoutStep === "payment" && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground-950"><a id="payment-method">Ödeme Yöntemi</a></h4>
                    <p className="text-xs text-foreground-500 mt-0.5">{cart.length} ürün · ₺{totalAmount.toFixed(2)}</p>
                  </div>
                  <button onClick={() => setShowCheckout(false)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                {/* Cart summary */}
                <div className="bg-background-100 rounded-xl p-3 mb-4 max-h-32 overflow-y-auto space-y-1.5">
                  {cart.map((item) => (
                    <div key={item.product.id} className="flex items-center justify-between text-xs">
                      <span className="text-foreground-600 truncate flex-1">
                        <span className="font-medium">{item.quantity}x</span> {item.product.name}
                      </span>
                      <span className="text-foreground-900 font-medium ml-2">₺{(item.customPrice * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 mb-4">
                  {paymentMethods.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setPaymentMethod(m.id)}
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                        paymentMethod === m.id
                          ? `${m.bg} ${m.border} border ${m.color}`
                          : "bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${m.bg}`}>
                        <i className={`${m.icon} text-lg ${m.color}`}></i>
                      </div>
                      <span className="flex-1 text-left">{m.label}</span>
                      {paymentMethod === m.id && <i className="ri-checkbox-circle-fill text-lg"></i>}
                    </button>
                  ))}
                </div>

                {paymentMethod === "kismi_odeme" && (
                  <div className="mb-4 p-4 bg-violet-50 border border-violet-200 rounded-xl">
                    <label className="block text-xs font-medium text-violet-700 mb-2">Alınan Kısmi Ödeme Tutarı</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-500 font-medium text-sm">₺</span>
                      <input
                        type="number"
                        value={partialAmount}
                        onChange={(e) => setPartialAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-8 pr-4 py-2.5 bg-background-50 border border-violet-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      />
                    </div>
                    {partialAmount && Number(partialAmount) > 0 && (
                      <p className="text-xs text-violet-600 mt-2 flex items-center gap-1">
                        <i className="ri-information-line"></i>
                        Kalan bakiye: ₺{(totalAmount - Number(partialAmount)).toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCheckout(false)}
                    className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    İptal
                  </button>
                  <button
                    onClick={goToRoomInfo}
                    disabled={!paymentMethod}
                    className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    Devam Et
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Room Number + Customer Name */}
            {checkoutStep === "room-info" && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-foreground-950">
                    <a id="room-info">Oda / Müşteri Bilgisi</a>
                  </h4>
                  <button onClick={() => setShowCheckout(false)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <div className="bg-secondary-100/50 border border-secondary-200/70 rounded-xl p-4 mb-4">
                  <p className="text-xs text-foreground-600 flex items-start gap-2">
                    <i className="ri-information-line text-secondary-600 shrink-0 mt-0.5"></i>
                    <span><strong>Oda Numarası</strong> ve <strong>Müşteri Adı</strong> alanlarının <strong>her ikisi de</strong> doldurulmak zorundadır.</span>
                  </p>
                </div>

                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                      <i className="ri-hotel-bed-line mr-1"></i>Oda Numarası <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={roomNumber}
                      onChange={(e) => { setRoomNumber(e.target.value); setRoomError(""); }}
                      placeholder="Örn: 104"
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1.5">
                      <i className="ri-user-line mr-1"></i>Müşteri Adı <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => { setCustomerName(e.target.value); setRoomError(""); }}
                      placeholder="Müşteri adı soyadı"
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-3 mb-4">
                  <div className="text-xs text-foreground-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Ödeme Yöntemi:</span>
                      <span className="font-medium text-foreground-900">{paymentLabel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Toplam Tutar:</span>
                      <span className="font-bold text-foreground-900">₺{totalAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>İşlemi Yapan:</span>
                      <span className="font-medium text-foreground-900">
                        {actingStaffName}{actingStaffRole ? ` (${actingStaffRole})` : ""}
                      </span>
                    </div>
                  </div>
                </div>

                {(roomError || saveError) && (
                  <div className="mb-4 p-3 bg-accent-100 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {roomError || saveError}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => setCheckoutStep("payment")}
                    className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-1"></i> Geri
                  </button>
                  <button
                    onClick={confirmSale}
                    disabled={saving}
                    className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Kaydediliyor...
                      </span>
                    ) : (
                      <>
                        <i className="ri-check-line mr-1"></i> Satışı Onayla
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/30" onClick={() => setShowAddCategoryModal(false)}>
          <div className="bg-background-50 rounded-t-2xl lg:rounded-2xl p-6 w-full max-w-sm mx-0 lg:mx-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-foreground-950">Yeni Kategori Ekle</h4>
              <button onClick={() => setShowAddCategoryModal(false)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <label className="block text-xs font-medium text-foreground-500 mb-1">Kategori Adı</label>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => { setNewCategoryName(e.target.value); setAddCategoryError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }}
              placeholder="Örn: Tatlılar"
              autoFocus
              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 mb-4"
            />

            {addCategoryError && (
              <div className="mb-4 p-3 bg-accent-100 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {addCategoryError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setShowAddCategoryModal(false)}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                İptal
              </button>
              <button
                onClick={addCategory}
                disabled={savingCategory}
                className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingCategory ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </span>
                ) : (
                  <>
                    <i className="ri-check-line mr-1"></i> Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/30" onClick={() => setShowAddProductModal(false)}>
          <div className="bg-background-50 rounded-t-2xl lg:rounded-2xl p-6 w-full max-w-lg mx-0 lg:mx-4 max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-foreground-950">Yeni Ürün Ekle</h4>
              <button onClick={() => setShowAddProductModal(false)} className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Adı *</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => { setNewProduct({ ...newProduct, name: e.target.value }); setAddProductError(""); }}
                  placeholder="Örn: Cheeseburger"
                  autoFocus
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Kategori</label>
                  <select
                    value={newProduct.categoryId}
                    onChange={(e) => setNewProduct({ ...newProduct, categoryId: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                  >
                    <option value="">Kategorisiz</option>
                    {dbCategories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Barkod</label>
                  <input
                    type="text"
                    value={newProduct.barcode}
                    onChange={(e) => setNewProduct({ ...newProduct, barcode: e.target.value })}
                    placeholder="Opsiyonel"
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Satış Fiyatı *</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={newProduct.salePrice}
                    onChange={(e) => { setNewProduct({ ...newProduct, salePrice: e.target.value }); setAddProductError(""); }}
                    placeholder="0.00"
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Alış Fiyatı</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={newProduct.purchasePrice}
                    onChange={(e) => setNewProduct({ ...newProduct, purchasePrice: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Stok</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={newProduct.stock}
                    onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>
            </div>

            {addProductError && (
              <div className="mb-4 p-3 bg-accent-100 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {addProductError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setShowAddProductModal(false)}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                İptal
              </button>
              <button
                onClick={addProduct}
                disabled={savingProduct}
                className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingProduct ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Kaydediliyor...
                  </span>
                ) : (
                  <>
                    <i className="ri-check-line mr-1"></i> Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}